/**
 * GLB skeleton → canonical pose points.
 *
 * Rig naming is all over the place (mixamo `mixamorig:LeftForeArm`, rigify/MPFB
 * `lowerarm01.L`, generic `Left Elbow`), so bones are classified by ordered regex rules on a
 * normalised name plus a side detector. Bone *origins* are the joint centres we want:
 * `LeftArm` starts at the shoulder, `LeftForeArm` at the elbow, and so on.
 *
 * Deliberately three.js-free so it can be unit tested under `node --experimental-strip-types`.
 */
import type { CanonicalPoint, PointMap, Vec3 } from "./types.ts";

export type Side = "left" | "right" | null;

/** Point kinds a bone can resolve to (sided ones get the left_/right_ prefix). */
type SidedKind =
  | "shoulder"
  | "elbow"
  | "wrist"
  | "index"
  | "hip"
  | "knee"
  | "ankle"
  | "foot_index"
  | "heel";

interface Rule {
  kind: SidedKind | "nose";
  test: RegExp;
  /** Lower wins when several bones claim the same point. */
  rank: number;
  /** Rules that only make sense for a sided bone. */
  requireSide?: boolean;
}

const RULES: Rule[] = [
  { kind: "elbow", test: /(forearm|lowerarm|lower_arm|ulna|radius|elbow)/, rank: 0, requireSide: true },
  { kind: "shoulder", test: /(upperarm|upper_arm|humerus)/, rank: 0, requireSide: true },
  { kind: "shoulder", test: /^arm\d*$/, rank: 1, requireSide: true },
  { kind: "index", test: /(handindex|indexfinger|f_index|index\d*$|finger)/, rank: 0, requireSide: true },
  { kind: "wrist", test: /(hand|wrist|palm)/, rank: 1, requireSide: true },
  { kind: "hip", test: /(upleg|upperleg|upper_leg|thigh|femur)/, rank: 0, requireSide: true },
  { kind: "knee", test: /(lowerleg|lower_leg|shin|calf|tibia|knee)/, rank: 0, requireSide: true },
  { kind: "knee", test: /^leg\d*$/, rank: 1, requireSide: true },
  { kind: "heel", test: /heel/, rank: 0, requireSide: true },
  { kind: "foot_index", test: /(toebase|toes|toe\d*|ball)/, rank: 0, requireSide: true },
  { kind: "ankle", test: /(foot|ankle)/, rank: 1, requireSide: true },
  { kind: "hip", test: /^hip\d*$/, rank: 2, requireSide: true },
  { kind: "shoulder", test: /(shoulder|clavicle|collar)/, rank: 3, requireSide: true },
  { kind: "nose", test: /^(head|face|nose)\d*$/, rank: 0 },
];

/** Strip rig prefixes/namespaces and punctuation, returning the comparable base name. */
export function normalizeBoneName(raw: string): { base: string; side: Side; order: number } {
  let name = (raw || "").toLowerCase();
  // Namespaces: "mixamorig:LeftArm", "Armature|Bone", "rig_Left_Arm".
  name = name.replace(/^.*[:|]/, "");
  name = name.replace(/^mixamorig\d*/, "");
  name = name.replace(/^(def|org|mch|ctrl|bone)[-_.]/, "");
  // Blender duplicate suffix ".001".
  name = name.replace(/\.\d{3}$/, "");

  let side: Side = null;
  if (/right/.test(name)) side = "right";
  else if (/left/.test(name)) side = "left";
  else if (/[._-]r(\.\d+)?$/.test(name)) side = "right";
  else if (/[._-]l(\.\d+)?$/.test(name)) side = "left";
  else if (/^r[._-]/.test(name)) side = "right";
  else if (/^l[._-]/.test(name)) side = "left";

  let base = name
    .replace(/left|right/g, "")
    .replace(/[._-][lr](\.\d+)?$/, "")
    .replace(/^[lr][._-]/, "")
    .replace(/[^a-z0-9]/g, "");

  // Trailing chain index ("upperarm01", "thigh02"): proximal segments come first.
  let order = 0;
  const m = base.match(/^(.*?)(\d{1,2})$/);
  if (m && m[1] && m[1].length > 2) {
    base = m[1];
    order = Number(m[2]);
  }
  return { base, side, order };
}

interface Candidate {
  name: string;
  rank: number;
  order: number;
  length: number;
}

function better(a: Candidate, b: Candidate): Candidate {
  if (a.rank !== b.rank) return a.rank < b.rank ? a : b;
  if (a.order !== b.order) return a.order < b.order ? a : b;
  return a.length <= b.length ? a : b;
}

/**
 * Map a list of bone names to canonical pose points.
 * Returns only the points the rig actually provides.
 */
export function mapBoneNames(names: string[]): Partial<Record<CanonicalPoint, string>> {
  const best = new Map<CanonicalPoint, Candidate>();
  for (const raw of names ?? []) {
    if (!raw) continue;
    const { base, side, order } = normalizeBoneName(raw);
    if (!base) continue;
    for (const rule of RULES) {
      if (!rule.test.test(base)) continue;
      if (rule.requireSide && !side) continue;
      const point = (
        rule.kind === "nose" ? "nose" : `${side}_${rule.kind}`
      ) as CanonicalPoint;
      // Leaf "tip" bones (mixamo `Toe_End`, `HeadTop_End`) only mark an end point; prefer
      // the real joint bone when both exist.
      const tipPenalty = /end$/.test(base) ? 5 : 0;
      const candidate: Candidate = {
        name: raw,
        rank: rule.rank + tipPenalty,
        order,
        length: base.length,
      };
      const current = best.get(point);
      best.set(point, current ? better(current, candidate) : candidate);
      break; // first matching rule classifies the bone
    }
  }
  const out: Partial<Record<CanonicalPoint, string>> = {};
  for (const [point, candidate] of best) out[point] = candidate.name;
  return out;
}

export interface BonePosition {
  name: string;
  position: Vec3;
}

/**
 * Build canonical points from bone world positions.
 * `mapping` may be precomputed once per human (bone names never change after load).
 */
export function pointsFromBones(
  bones: BonePosition[],
  mapping?: Partial<Record<CanonicalPoint, string>>,
): PointMap {
  const map = mapping ?? mapBoneNames(bones.map((b) => b.name));
  const byName = new Map(bones.map((b) => [b.name, b.position]));
  const out: PointMap = {};
  for (const [point, boneName] of Object.entries(map)) {
    const p = boneName ? byName.get(boneName) : undefined;
    if (p) out[point as CanonicalPoint] = [p[0], p[1], p[2]];
  }
  return out;
}

/** base / flyer role guess from a human id (matches poseExport's heuristic). */
export function guessRole(humanId: string | null | undefined): "base" | "flyer" {
  const id = (humanId || "").toLowerCase();
  return id.includes("flyer") ? "flyer" : "base";
}
